// Client dispatcher for AI write-tool proposals (Phase 2, docs/AI_PHASE2_PLAN.md).
//
// Split for testability:
//   buildQuestApplyPlan()   PURE — normalizes/validates the tool input against
//                           the user's checkbox selection into an exact plan
//                           of store operations (unit-tested).
//   executeQuestApplyPlan() runs the plan against the real stores.
//   spendCoin()             the charge-at-Apply gate — server-authoritative
//                           debit via the spend_coin RPC (migration 0196);
//                           inactive/unpriced key → free, ok=true.
//
// Order at Apply: spendCoin FIRST; only on ok run the stores. On insufficient
// Coin nothing is created and the card shows the shortfall.

import { supabase } from '../lib/supabase';
import { useQuestStore } from '../stores/questStore';
import { useHabitStore } from '../stores/habitStore';
import { useTaskStore } from '../stores/taskStore';
import { useWalletStore } from '../stores/walletStore';
import { ensureCapacityForBatch, type CapacityRequest } from '../utils/capacityGuard';
import type { AIProposal } from './aiProxy';
import type { HabitSchedule } from '../types';
import { pickMissingDays } from '../utils/habitDaysPrompt';

const DOMAINS = [
  'personal', 'social', 'occupational', 'intellectual',
  'avocational', 'ecological', 'existential',
];

const normalizeDomain = (c?: string): string => {
  const lc = String(c ?? '').toLowerCase().trim();
  return DOMAINS.includes(lc) ? lc : 'personal';
};

// ── plan types ───────────────────────────────────────────────────────────────

export interface PlannedLinkedAction {
  kind: 'new_habit' | 'new_task' | 'existing_habit' | 'existing_task';
  name?: string;
  description?: string;
  interval?: 'daily' | 'weekly';
  /** The days the person picked for a weekly new habit (set by `applyProposal`). */
  schedule?: HabitSchedule;
  priority?: 'high' | 'medium' | 'low';
  duration?: number;
  id?: string;
}

export interface PlannedGoal {
  actionVerb: string;
  what: string;
  quantity: number;
  byWhen?: string;
  linked?: PlannedLinkedAction;
}

export interface QuestApplyPlan {
  quest: { title: string; description: string; category: string };
  goals: PlannedGoal[];
}

/** Per-goal selection; index-aligned with the proposal's goals array. */
export interface QuestApplySelection {
  goals: boolean[];
  /** Only honored where the goal itself is selected. */
  linkedActions: boolean[];
}

export const selectAll = (input: any): QuestApplySelection => {
  const n = Array.isArray(input?.goals) ? input.goals.length : 0;
  return { goals: Array(n).fill(true), linkedActions: Array(n).fill(true) };
};

// ── pure plan builder ────────────────────────────────────────────────────────

export function buildQuestApplyPlan(input: any, selection: QuestApplySelection): QuestApplyPlan | null {
  const title = typeof input?.title === 'string' ? input.title.trim() : '';
  if (!title) return null;

  const rawGoals: any[] = Array.isArray(input?.goals) ? input.goals : [];
  const goals: PlannedGoal[] = [];
  rawGoals.forEach((g, i) => {
    if (!selection.goals[i]) return;
    const actionVerb = typeof g?.actionVerb === 'string' ? g.actionVerb.trim() : '';
    const what = typeof g?.what === 'string' ? g.what.trim() : '';
    if (!actionVerb || !what) return;

    let linked: PlannedLinkedAction | undefined;
    const la = g?.linkedAction;
    if (selection.linkedActions[i] && la && typeof la === 'object') {
      const kind = la.kind;
      const isNew = kind === 'new_habit' || kind === 'new_task';
      const isExisting = kind === 'existing_habit' || kind === 'existing_task';
      // A new action needs a name; an existing link needs an id — else drop it.
      if ((isNew && typeof la.name === 'string' && la.name.trim()) || (isExisting && typeof la.id === 'string' && la.id)) {
        linked = {
          kind,
          name: typeof la.name === 'string' ? la.name.trim() : undefined,
          description: typeof la.description === 'string' ? la.description : undefined,
          interval: la.interval === 'weekly' ? 'weekly' : 'daily',
          priority: ['high', 'medium', 'low'].includes(la.priority) ? la.priority : 'medium',
          duration: Number.isFinite(Number(la.duration)) && Number(la.duration) > 0 ? Math.round(Number(la.duration)) : 30,
          id: typeof la.id === 'string' ? la.id : undefined,
        };
      }
    }

    goals.push({
      actionVerb,
      what,
      quantity: Number.isFinite(Number(g?.quantity)) && Number(g.quantity) > 0 ? Math.round(Number(g.quantity)) : 1,
      byWhen: typeof g?.byWhen === 'string' && /^\d{4}-\d{2}-\d{2}/.test(g.byWhen) ? g.byWhen.slice(0, 10) : undefined,
      linked,
    });
  });

  if (goals.length === 0) return null;
  return {
    quest: {
      title,
      description: typeof input?.description === 'string' ? input.description : '',
      category: normalizeDomain(input?.category),
    },
    goals,
  };
}

// ── spend gate ───────────────────────────────────────────────────────────────

export interface SpendResult {
  ok: boolean;
  balanceCp: number;
  priceCp: number;
}

/**
 * Debit the wallet for one apply.
 *
 * ⚠️⚠️ **`p_qty` IS OMITTED AT ONE, DELIBERATELY.** `spend_coin(text, int)`
 * arrives with migration 0246; sending the second argument to a database that
 * has not run it yet is a 404 from PostgREST, not a graceful fallback. Omitting
 * it makes every existing call byte-identical to the one it made yesterday, so
 * nothing that shipped before 0246 can break.
 *
 * ⭐ And the quantity path is self-gating: a caller only asks for `qty > 1`
 * after reading a LIVE price for the key, and a key cannot be active unless the
 * migration that created its row has run. The dependency enforces itself.
 */
export async function spendCoin(actionKey: string, qty = 1): Promise<SpendResult> {
  const args: Record<string, unknown> = { p_action_key: actionKey };
  if (qty > 1) args.p_qty = qty;
  const { data, error } = await supabase.rpc('spend_coin', args);
  if (error) throw new Error(`Coin charge failed: ${error.message}`);
  const row = Array.isArray(data) ? data[0] : data;
  const result = {
    ok: row?.ok === true,
    balanceCp: Number(row?.balance_cp ?? 0),
    priceCp: Number(row?.price_cp ?? 0),
  };
  if (result.ok && result.priceCp > 0) void useWalletStore.getState().refresh();
  return result;
}

// ── store executor ───────────────────────────────────────────────────────────

export interface QuestApplyOutcome {
  questId: string;
  goalsCreated: number;
  newHabits: number;
  newTasks: number;
  existingLinks: number;
}

export function executeQuestApplyPlan(plan: QuestApplyPlan): QuestApplyOutcome {
  const questStore = useQuestStore.getState();
  const habitStore = useHabitStore.getState();
  const taskStore = useTaskStore.getState();

  const folderId = questStore.folders[0]?.id ?? '';
  const questId = questStore.addQuest({
    title: plan.quest.title,
    description: plan.quest.description,
    category: plan.quest.category,
    folderId,
    status: 'active',
  });

  const outcome: QuestApplyOutcome = { questId, goalsCreated: 0, newHabits: 0, newTasks: 0, existingLinks: 0 };

  for (const g of plan.goals) {
    let linkType: 'habit' | 'task' | undefined;
    let linkId: string | undefined;

    if (g.linked) {
      const la = g.linked;
      if (la.kind === 'new_habit' && la.name) {
        linkId = habitStore.addHabit({
          name: la.name,
          description: la.description || '',
          category: plan.quest.category,
          icon: 'Sparkles',
          schedule: la.schedule ?? { interval: la.interval ?? 'daily' },
        } as any);
        linkType = 'habit';
        outcome.newHabits++;
      } else if (la.kind === 'new_task' && la.name) {
        linkId = crypto.randomUUID();
        taskStore.addTask({
          id: linkId,
          title: la.name,
          description: la.description || '',
          category: plan.quest.category,
          priority: la.priority ?? 'medium',
          duration: la.duration ?? 30,
          completed: false,
        } as any);
        linkType = 'task';
        outcome.newTasks++;
      } else if (la.kind === 'existing_habit' && la.id) {
        // Link only if the item is really there — the model may hallucinate ids.
        if (habitStore.habits.some((h: any) => h.id === la.id)) {
          linkType = 'habit';
          linkId = la.id;
          outcome.existingLinks++;
        }
      } else if (la.kind === 'existing_task' && la.id) {
        if (taskStore.getTaskById(la.id)) {
          linkType = 'task';
          linkId = la.id;
          outcome.existingLinks++;
        }
      }
    }

    questStore.addGoal(questId, {
      actionVerb: g.actionVerb,
      what: g.what,
      quantity: g.quantity,
      byWhen: g.byWhen,
      ...(linkType && linkId ? { linkType, linkId } : {}),
    } as any);
    outcome.goalsCreated++;
  }

  return outcome;
}

// ── top-level apply ──────────────────────────────────────────────────────────

/**
 * Everything this plan would CREATE, as capacity requests — pure, so the
 * pre-flight can be reasoned about (and tested) without touching a store.
 *
 * ⭐ ONE REQUEST PER TYPE, carrying a COUNT. A plan can attach four new habits
 * to four goals; asking "is there room for one more habit" four times passes at
 * three-of-five while the plan as a whole does not fit.
 *
 * ⚠️ GOALS ARE NOT LISTED, deliberately. They land inside the quest this plan is
 * about to create, so their per-quest holding is 0 and the quest they belong to
 * is already the thing being checked — the same rule every other create path
 * follows for children of a brand-new parent.
 * ⚠️ `existing_habit` / `existing_task` links create NOTHING; only the `new_*`
 * kinds count.
 */
export function planCapacityRequests(plan: QuestApplyPlan): CapacityRequest[] {
  const domainId = plan.quest.category;
  const newHabits = plan.goals.filter(g => g.linked?.kind === 'new_habit').length;
  const newTasks = plan.goals.filter(g => g.linked?.kind === 'new_task').length;

  return [
    { type: 'quests', domainId, adding: 1, draftTitle: plan.quest.title, draftDescription: plan.quest.description },
    { type: 'habits', domainId, adding: newHabits },
    { type: 'tasks', domainId, adding: newTasks },
    // ⚠️ `adding: 0` entries are skipped by `ensureCapacity`, so a plan with no
    // linked actions is never measured against a full habit allowance.
  ];
}

export type ApplyProposalResult =
  | { status: 'applied'; outcome: QuestApplyOutcome; spend: SpendResult }
  | { status: 'insufficient_coin'; spend: SpendResult }
  /** Capacity refused the plan. Nothing created, nothing charged. */
  | { status: 'capacity_blocked' }
  /** The person cancelled picking a weekly habit's days. Nothing created, nothing charged. */
  | { status: 'cancelled' }
  | { status: 'invalid' };

export async function applyProposal(proposal: AIProposal, selection: QuestApplySelection): Promise<ApplyProposalResult> {
  if (proposal.capability !== 'create_quest') return { status: 'invalid' };
  const plan = buildQuestApplyPlan(proposal.input, selection);
  if (!plan) return { status: 'invalid' };

  /* ⚠️⚠️ THE DAYS COME FIRST — before the capacity pre-flight and `spendCoin`
     (owner, 2026-10-04). A weekly new habit used to land with NO day, never due
     (utils/habitSchedule.ts). Cancelling any picker stops the apply, uncharged. */
  const newHabits = plan.goals.map(g => g.linked).filter((la): la is PlannedLinkedAction => la?.kind === 'new_habit' && !!la.name);
  const schedules = await pickMissingDays(newHabits.map(la => ({ name: la.name!, schedule: { interval: la.interval ?? 'daily' } })));
  if (!schedules) return { status: 'cancelled' };
  newHabits.forEach((la, i) => { la.schedule = schedules[i]; });

  /**
   * ⚠️⚠️ THE PRE-FLIGHT RUNS BEFORE `spendCoin`, AND THAT ORDER IS THE WHOLE
   * POINT. `executeQuestApplyPlan` returns a non-nullable outcome and creates a
   * quest, its goals and their linked actions in one pass, so a refusal
   * discovered inside it would arrive AFTER the debit and after some records
   * existed. Checking the whole plan up front is what makes the refusal free.
   */
  if (!ensureCapacityForBatch(planCapacityRequests(plan))) return { status: 'capacity_blocked' };

  const spend = await spendCoin('create_quest');
  if (!spend.ok) return { status: 'insufficient_coin', spend };

  return { status: 'applied', outcome: executeQuestApplyPlan(plan), spend };
}
